import { ArrowUpRight, Braces, Copy, FileText, List } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { JsonTable } from "~/components/json-table";
import { SectionCard } from "~/components/section-card";
import { Badge, Button } from "~/components/ui";
import type { EvidenceActivity, EvidenceComposition, EvidenceDetailUI } from "~/features/evidences/types";

interface EvidenceDataTabProps {
  detail: EvidenceDetailUI;
}

export function EvidenceDataTab({ detail }: EvidenceDataTabProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <SectionCard
        icon={FileText}
        title={t("model.evidence.modelName")}
        contentClassName="divide-y divide-dashed divide-border-default py-0 text-[13px]"
      >
        <Row label={t("model.reference.modelName")}>
          {detail.reference ? (
            <span className="inline-flex items-center gap-1.5 text-fg-1">
              {detail.reference.title}
              <Link to={`/references/${detail.reference.id}`} aria-label={t("ui.detail")} className="text-fg-link">
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </span>
          ) : (
            <span className="text-fg-3">—</span>
          )}
        </Row>

        <Row label={t("model.evidence.citation")}>
          <div className="max-w-full overflow-x-auto">
            <JsonTable target={detail.citation} />
          </div>
        </Row>

        <Row label={t("ui.createdBy")}>
          {detail.activity ? <ActivityTable activity={detail.activity} /> : <span className="text-fg-3">—</span>}
        </Row>

        <Row label={t("ui.compositions")}>
          {detail.compositions.length > 0 ? (
            <div className="space-y-3">
              {detail.compositions.map((c) => (
                <CompositionTable key={c.key} composition={c} />
              ))}
            </div>
          ) : (
            <span className="text-fg-3">—</span>
          )}
        </Row>
      </SectionCard>

      <SectionCard icon={List} title={t("model.common.properties")}>
        <JsonTable target={detail.properties} />
      </SectionCard>

      <SectionCard icon={Braces} title={t("model.common.metadata")}>
        <JsonTable target={detail.metadata} />
      </SectionCard>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(6rem,9rem)_1fr] items-start gap-4 py-2.5">
      <span className="text-fg-3">{label}</span>
      <div className="min-w-0 text-fg-1">{children}</div>
    </div>
  );
}

function CopyIdButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const onCopy = async () => {
    try {
      await copyToClipboard(value);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-5 w-5 text-fg-3"
      onClick={onCopy}
      aria-label={t("ui.copyId")}
    >
      <Copy className="h-3 w-3" />
    </Button>
  );
}

/** Provenance of how this evidence was created (data-import-project → data-source → batch-evidences job). */
function ActivityTable({ activity }: { activity: EvidenceActivity }) {
  const { t } = useTranslation();
  return (
    <table className="w-full max-w-2xl border-collapse text-[13px]">
      <tbody>
        {activity.jobs.map((job) => (
          <SubRow key={job.id} label={t("model.batchEvidencesJob.modelName")}>
            <div className="space-y-0.5">
              {job.dataImportProjectId && (
                <div>
                  <span className="text-12-bold text-fg-3">{t("model.dataImportProject.modelName")}: </span>
                  <Link to={`/data-import-projects/${job.dataImportProjectId}`} className="text-fg-link">
                    {job.dataImportProjectName || job.dataImportProjectId}
                  </Link>
                </div>
              )}
              {job.dataImportProjectId && job.dataSourceId && (
                <div>
                  <span className="text-12-bold text-fg-3">{t("model.dataSource.modelName")}: </span>
                  <Link
                    to={`/data-import-projects/${job.dataImportProjectId}?${new URLSearchParams({ peekType: "dataSource", peekId: job.dataSourceId })}`}
                    className="text-fg-link"
                  >
                    {job.dataSourceName || job.dataSourceId}
                  </Link>
                </div>
              )}
              {job.dataImportProjectId && (
                <div>
                  <span className="text-12-bold text-fg-3">{t("model.batchEvidencesJob.modelName")}: </span>
                  <Link
                    to={`/data-import-projects/${job.dataImportProjectId}?${new URLSearchParams({ peekType: "batchEvidencesJob", peekId: job.id })}`}
                    className="text-fg-link"
                  >
                    {job.name || job.id}
                  </Link>
                </div>
              )}
            </div>
          </SubRow>
        ))}
        <SubRow label={t("model.evidenceSet.modelName")}>
          <span className="inline-flex items-center gap-1 tabular-nums text-fg-1">
            {activity.setName || `(${t("ui.noName")})`}
            <CopyIdButton value={activity.setId} />
          </span>
        </SubRow>
      </tbody>
    </table>
  );
}

/** Records this evidence helped compose, via a batch-change-requests job + change request. */
function CompositionTable({ composition: c }: { composition: EvidenceComposition }) {
  const { t } = useTranslation();
  return (
    <table className="w-full max-w-2xl border-collapse text-[13px]">
      <tbody>
        <SubRow label={t("model.dataImportProject.modelName")}>
          {c.dataImportProjectId ? (
            <Link to={`/data-import-projects/${c.dataImportProjectId}`} className="text-fg-link">
              {c.dataImportProjectName || c.dataImportProjectId}
            </Link>
          ) : (
            <span className="text-fg-3">—</span>
          )}
        </SubRow>
        <SubRow label={t("model.batchChangeRequestsJob.modelName")}>
          <span className="inline-flex items-center gap-1 tabular-nums text-fg-1">
            {c.jobName || `(${t("ui.noName")})`}
            <CopyIdButton value={c.jobId} />
          </span>
        </SubRow>
        {c.jobRunId && (
          <SubRow label={t("model.batchChangeRequestsJobRun.modelName")}>
            <span className="inline-flex items-center gap-1 tabular-nums text-fg-1">
              {c.jobRunId}
              <CopyIdButton value={c.jobRunId} />
            </span>
          </SubRow>
        )}
        <SubRow label={t("model.changeRequest.modelName")}>
          <span className="inline-flex items-center gap-1.5">
            <Badge tone="default">{c.statusId}</Badge>
            <Link to={`/change-requests/${c.changeRequestId}`} aria-label={t("ui.detail")} className="text-fg-link">
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </span>
        </SubRow>
        {c.recordMetadataIds.map((id) => (
          <SubRow key={id} label={t("model.recordMetadata.modelName")}>
            <span className="inline-flex items-center gap-1 tabular-nums text-fg-1">
              {id}
              <CopyIdButton value={id} />
            </span>
          </SubRow>
        ))}
        {c.entityRecords.map((r) => (
          <SubRow key={r.id} label={t("model.record.entity.modelName")}>
            <span className="inline-flex items-center gap-1.5 text-fg-1">
              {r.name}
              <Link to={`/entities/${r.id}`} aria-label={t("ui.detail")} className="text-fg-link">
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </span>
          </SubRow>
        ))}
        {c.relationRecords.map((r) => (
          <SubRow key={r.id} label={t("model.record.relation.modelName")}>
            <span className="inline-flex items-center gap-1.5 text-fg-1">
              {r.name}
              <Link to={`/relations/${r.id}`} aria-label={t("ui.detail")} className="text-fg-link">
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </span>
          </SubRow>
        ))}
      </tbody>
    </table>
  );
}

function SubRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr className="border-b border-border-default align-top last:border-b-0">
      <th className="w-48 whitespace-nowrap py-2 pr-3 text-left align-top font-bold text-fg-2">{label}</th>
      <td className="py-2 text-fg-2">{children}</td>
    </tr>
  );
}
